// src/pages/Users/Inscription/InscriptionAdherent.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Button from "./../../../components/Button/Button";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import { getAdherentsByUser } from "./../../../services/adherentServices";
import { getDossierInscription } from "./../../../services/dossierServices";
import { getGroupName } from "./../../../services/groupServices";
import { getAllSegments } from "./../../../services/segmentDossierServices";
import { getSettings } from "./../../../services/settingsServices";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import AdherentCards from "./AdherentCard";
import RenewalModal from "./RenewalModal";
import useScrollToTop from "./../../../hooks/useScollToTop";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import { ToastContainer, toast } from "react-toastify";
import {
  FaFileDownload,
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaPlus,
  FaAddressCard
} from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";

/* ---------- UI helpers ---------- */
function LoadingRowSkeleton() {
  return (
    <div className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24" />
  );
}

// --- Helpers tags ---
function getStatusClass(statut) {
  switch (statut) {
    case "Licencié":
      return "bg-green-300 text-slate-800";
    case "Licencié Hors Club":
      return "bg-green-200 text-slate-800";
    case "Inscrit":
      return "bg-yellow-300 text-slate-900";
    case "Cours d'essai":
      return "bg-red-300 text-slate-900";
    case "Licencié - Arrêt":
      return "bg-slate-300 text-slate-800";
    case "Ancien Licencié et Arrêt":
      return "bg-slate-300 text-slate-800";
    case "Ancien Licencié":
      return "bg-slate-900 text-white";
    case "Non Licencié - Arrêt":
      return "bg-slate-500 text-white";
    default:
      return "bg-slate-200 text-slate-800";
  }
}

function getStatusBorderClass(statut) {
  switch (statut) {
    case "Licencié":
      return "border-green-300";
    case "Licencié Hors Club":
      return "border-green-200";
    case "Inscrit":
      return "border-yellow-300";
    case "Cours d'essai":
      return "border-red-300";
    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
      return "border-slate-300";
    case "Ancien Licencié":
      return "border-slate-900";
    case "Non Licencié - Arrêt":
      return "border-slate-500";
    default:
      return "border-slate-200";
  }
}

const seasonTagClass = (saison, current) =>
  saison && current && saison === current
    ? "bg-emerald-200 text-emerald-900 ring-1 ring-emerald-300"
    : "bg-slate-200 text-slate-800 ring-1 ring-slate-300";

const Dropdown = ({ title, children, isOpen, toggle }) => (
  <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
    <button
      onClick={toggle}
      className="w-full flex justify-between items-center text-left px-5 py-4 text-base sm:text-lg font-semibold text-slate-800 dark:text-slate-100"
    >
      <span className="truncate">{title}</span>
      {isOpen ? <FaChevronUp /> : <FaChevronDown />}
    </button>
    {isOpen && <div className="px-5 pb-5">{children}</div>}
  </div>
);

/* ---------- Page ---------- */
const InscriptionAdherent = () => {
  useScrollToTop();
  const { user } = useUser();
  const navigate = useNavigate();

  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId;

  const [loading, setLoading] = useState(true);
  const [adherents, setAdherents] = useState([]);
  const [dossiers, setDossiers] = useState({});
  const [groupNames, setGroupNames] = useState({});
  const [segments, setSegments] = useState([]);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [search, setSearch] = useState("");
  const [showDetailed, setShowDetailed] = useState(false);

  const [activeSeason, setActiveSeason] = useState("");
  const [expanded, setExpanded] = useState({}); // { [adherentId]: bool }

  const [showRenew, setShowRenew] = useState(false);
  const [renewAdherent, setRenewAdherent] = useState(null);

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  const toggleDropdown = (id) =>
    setOpenDropdowns((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleDownload = (fileUrl) => window.open(fileUrl, "_blank");

  /* ---------- Load ---------- */
  useEffect(() => {
    (async () => {
      if (!userId) return;
      try {
        setLoading(true);

        const adherentsData = await getAdherentsByUser(userId);
        setAdherents(Array.isArray(adherentsData) ? adherentsData : []);

        const dossiersData = await Promise.all(
          (adherentsData || []).map((a) => safeGetDossierInscription(a.id))
        );
        setDossiers(
          (adherentsData || []).reduce((acc, a, i) => {
            acc[a.id] = dossiersData[i]; // peut être null si 404
            return acc;
          }, {})
        );

        const groupPromises = (adherentsData || []).map((a) =>
          getGroupName(a.id)
        );
        const groupData = await Promise.all(groupPromises);
        setGroupNames(
          (adherentsData || []).reduce((acc, a, i) => {
            acc[a.id] = groupData[i];
            return acc;
          }, {})
        );

        // >>>> NOUVEAU : saison actuelle
        const settings = await getSettings();
        const current = settings?.[0]?.saison_active || "";
        setActiveSeason(current);

        const seg = await getAllSegments();
        setSegments(Array.isArray(seg) ? seg : []);
        const dropdownStates = {};
        (seg || []).forEach((s) => (dropdownStates[s.id] = false));
        setOpenDropdowns(dropdownStates);
      } catch (e) {
        console.error(e);
        toast.error("Erreur lors du chargement des données.");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const refreshDossier = async (adherentId) => {
    const data = await safeGetDossierInscription(adherentId);
    setDossiers((prev) => ({ ...prev, [adherentId]: data })); // data peut être null
  };

  /* ---------- Derived ---------- */
  const filteredAdherents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return adherents;
    return adherents.filter((a) =>
      `${a.nom || ""} ${a.prenom || ""}`.toLowerCase().includes(q)
    );
  }, [adherents, search]);

  const defaultAvatar = (genre) =>
    genre === "Féminin" ? "/img/judokate_avatar.png" : "/img/judoka_avatar.png";

  const toggleCard = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleRenew = (a) => {
    setRenewAdherent(a);
    setShowRenew(true);
  };

  // Helper: évite le crash si le dossier n'existe pas (404)
  const safeGetDossierInscription = async (adherentId) => {
    try {
      const data = await getDossierInscription(adherentId);
      return data; // objet dossier
    } catch (err) {
      const status = err?.response?.status ?? err?.status;
      if (status === 404) {
        // Dossier non créé pour cet adhérent
        return null; // on renverra null et on affichera un message
      }
      console.error("getDossierInscription failed:", err);
      toast.error("Erreur lors du chargement du dossier d’inscription.");
      return null;
    }
  };

  /* ---------- Render ---------- */
  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        

        <HeaderTitle
                  role="Utilisateur"
                  roleId="2"
                  title="Dossier d’inscription"
                  subtitle="Gérez les dossiers d’inscription et renouvelez les adhésions de vos adhérents."
                  icon={FaAddressCard}
                  align="left"
                  className="mb-4"
                />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-6">
          {/* Bandeau dégradé + actions */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Gérez les dossiers d’inscription de vos adhérents
              </h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                Visualisez rapidement vos adhérents, téléchargez les documents
                utiles et créez de nouveaux dossiers.
              </p>

              <div className="mt-5 flex items-center justify-center">
                <Button
                  text="Créer un nouvel adhérent"
                  icon={FaPlus}
                  onClick={() => navigate("/ajouter-inscription-adherent")}
                  className="!rounded-xl !shadow-md gap-2"
                />
              </div>
            </div>
          </div>

          {/* Filtres + compteur */}
          <div className="px-5 py-5 sm:px-8">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-end gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Recherche par nom / prénom
                </label>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Ex : Durand, Alice…"
                    className="w-full rounded-xl pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              <div className="text-sm font-medium text-slate-700 dark:text-slate-200 md:text-right">
                Adhérents affichés : {filteredAdherents.length} /{" "}
                {adherents.length}
              </div>
            </div>
          </div>

          {/* Liste adhérents - Table desktop / Cards mobile */}
          <div className="px-5 pb-6 sm:px-8">
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <LoadingRowSkeleton key={i} />
                ))}
              </div>
            ) : filteredAdherents.length === 0 ? (
              <div className="text-center py-12 text-slate-600 dark:text-slate-400">
                Aucun adhérent ne correspond à votre recherche.
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <table className="min-w-[900px] w-full text-sm">
                    <thead className="bg-slate-50/90 dark:bg-slate-900/70">
                      <tr className="text-left text-slate-700 dark:text-slate-200">
                        <th className="px-4 py-3">Photo</th>
                        <th className="px-4 py-3">Nom / Prénom</th>
                        <th className="px-4 py-3">Statut inscription</th>
                        <th className="px-4 py-3">Saison</th>
                        <th className="px-4 py-3 w-1/4">Groupe</th>{" "}
                        {/* optionnel: fixe un peu la largeur */}
                        <th className="px-4 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-2 00 dark:divide-slate-800">
                      {filteredAdherents.map((a) => (
                        <React.Fragment key={a.id}>
                          <tr className="bg-white/90 dark:bg-slate-900/40 hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors">
                            <td className="px-4 py-3">
                              <img
                                src={
                                  toAbsolute(a.photo_url) ||
                                  defaultAvatar(a.genre)
                                }
                                alt={`${a.nom || ""} ${a.prenom || ""}`}
                                className="h-12 w-12 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                              />
                            </td>

                            <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                              {(a.nom || "").toUpperCase()} {a.prenom || ""}
                            </td>

                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${getStatusClass(
                                  a.statut_inscription
                                )}`}
                              >
                                {a.statut_inscription || "—"}
                              </span>
                            </td>

                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${seasonTagClass(
                                  a.saison_active,
                                  activeSeason
                                )}`}
                              >
                                {a.saison_active || "—"}
                              </span>
                            </td>

                            <td className="px-4 py-3 text-slate-800 dark:text-slate-100 align-top">
                              <div className="whitespace-normal break-words leading-snug">
                                {groupNames[a.id]?.join(", ") || "Non attribué"}
                              </div>
                            </td>

                            <td className="px-4 py-3">
                              <div className="flex justify-start items-center gap-2">
                                <Button
                                  text={
                                    expanded[a.id]
                                      ? "▲ Masquer la fiche"
                                      : "▼ Voir la fiche"
                                  }
                                  onClick={() => toggleCard(a.id)}
                                  className="!rounded-xl !px-3 !py-1.5 !text-xs"
                                />
                                {activeSeason &&
                                  a.saison_active !== activeSeason && (
                                    <Button
                                      text="Renouveler l’adhésion"
                                      onClick={() => handleRenew(a)}
                                      className="!rounded-xl !px-3 !py-1.5 !text-xs"
                                    />
                                  )}
                              </div>
                            </td>
                          </tr>

                          {expanded[a.id] && (
                            <tr className="bg-white/60 dark:bg-slate-900/40">
                              <td colSpan={6} className="px-4 py-4">
                                {dossiers[a.id] === null && (
                                  <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm">
                                    Aucun dossier d’inscription trouvé pour cet
                                    adhérent (404). Il sera créé automatiquement
                                    lors de la prochaine inscription, ou par un
                                    administrateur.
                                  </div>
                                )}
                                <AdherentCards
                                  adherent={a}
                                  dossier={dossiers[a.id]} // peut être null, le message au-dessus évite l'effet "vide"
                                  user={user}
                                  refreshAdherents={async () => {
                                    try {
                                      setLoading(true);
                                      const data = await getAdherentsByUser(
                                        userId
                                      );
                                      setAdherents(
                                        Array.isArray(data) ? data : []
                                      );
                                    } finally {
                                      setLoading(false);
                                    }
                                  }}
                                  refreshDossier={refreshDossier}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {filteredAdherents.map((a) => (
                    <div
                      key={a.id}
                      className={`rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4
              bg-white/90 dark:bg-slate-900/40
              border-l-[15px] border-solid ${getStatusBorderClass(
                a.statut_inscription
              )}`}
                      title={`Statut inscription : ${
                        a.statut_inscription || "—"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={
                            toAbsolute(a.photo_url) || defaultAvatar(a.genre)
                          }
                          alt={`${a.nom || ""} ${a.prenom || ""}`}
                          className="h-12 w-12 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                        />
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                            {(a.nom || "").toUpperCase()} {a.prenom || ""}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${getStatusClass(
                                a.statut_inscription
                              )}`}
                            >
                              {a.statut_inscription || "—"}
                            </span>
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${seasonTagClass(
                                a.saison_active,
                                activeSeason
                              )}`}
                            >
                              {a.saison_active || "—"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                        <span className="opacity-70">Groupe :</span>{" "}
                        {groupNames[a.id]?.join(", ") || "Non attribué"}
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          text={
                            expanded[a.id]
                              ? "Masquer la fiche"
                              : "Voir la fiche"
                          }
                          onClick={() => toggleCard(a.id)}
                          className="!rounded-xl !px-3 !py-1.5 !text-xs"
                        />
                        {activeSeason && a.saison_active !== activeSeason && (
                          <Button
                            text="Renouveler l’adhésion"
                            onClick={() => handleRenew(a)}
                            className="!rounded-xl !px-3 !py-1.5 !text-xs"
                          />
                        )}
                      </div>

                      {expanded[a.id] && (
                        <tr className="bg-white/60 dark:bg-slate-900/40">
                          <td colSpan={6} className="px-4 py-4">
                            <div
                              className={`rounded-2xl ring-1 ring-slate-200 dark:ring-slate-700 p-4
                    bg-white/80 dark:bg-slate-900/60
                    `}
                              title={`Statut inscription : ${
                                a.statut_inscription || "—"
                              }`}
                            >
                              {dossiers[a.id] === null && (
                                <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm">
                                  Aucun dossier d’inscription trouvé pour cet
                                  adhérent (404).
                                </div>
                              )}
                              <AdherentCards
                                adherent={a}
                                dossier={dossiers[a.id]}
                                user={user}
                                refreshAdherents={async () => {
                                  /* ... */
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Segments du dossier (accordion) */}
        {/* Bandeau dégradé + actions */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 mb-4 rounded-t-2xl">
          <div className="px-5 py-6 sm:px-8 text-white">
            <h2 className="text-lg sm:text-xl font-semibold text-center">
              Informations et documents utiles
            </h2>
            <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
              Accédez à toutes les informations et documents nécessaires pour
              gérer les dossiers d’inscription de vos adhérents.
            </p>
          </div>
        </div>
        {(segments || []).map((segment) => (
          <Dropdown
            key={segment.id}
            title={segment.segment_name}
            isOpen={openDropdowns[segment.id]}
            toggle={() => toggleDropdown(segment.id)}
          >
            <div
              className="prose prose-sm max-w-none dark:prose-invert prose-headings:mt-0 prose-p:my-2 text-slate-800 dark:text-slate-100"
              dangerouslySetInnerHTML={{ __html: segment.segment_content }}
            />
            {segment.fichiers?.length > 0 && (
              <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap gap-2">
                {segment.fichiers.map((file) => (
                  <Button
                    key={file.id}
                    text={file.nom_fichier}
                    icon={FaFileDownload}
                    onClick={() => handleDownload(file.lien_fichier)}
                    hoverLabel={file.detail_fichier}
                    className="gap-2 !rounded-xl"
                  />
                ))}
              </div>
            )}
          </Dropdown>
        ))}
      </div>

      {showRenew && (
        <RenewalModal
          isOpen={showRenew}
          adherent={renewAdherent}
          nextSeason={activeSeason}
          onClose={async (saved /*, selectedGroup */) => {
            setShowRenew(false);
            setRenewAdherent(null);

            // Si l'enregistrement a réussi, on rafraîchit la liste
            if (saved) {
              try {
                setLoading(true);
                const data = await getAdherentsByUser(userId);
                setAdherents(Array.isArray(data) ? data : []);
              } finally {
                setLoading(false);
              }
            }
          }}
        />
      )}

      <ToastContainer />
    </section>
  );
};

export default InscriptionAdherent;
